import { useEffect, useRef } from "react";

function FloatingParticles() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    const particles: Array<{
      x: number;
      y: number;
      r: number;
      dx: number;
      dy: number;
      opacity: number;
    }> = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    for (let i = 0; i < 40; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * 3 + 1,
        dx: (Math.random() - 0.5) * 0.5,
        dy: (Math.random() - 0.5) * 0.5,
        opacity: Math.random() * 0.3 + 0.1,
      });
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(37, 99, 235, ${p.opacity})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 z-0 w-full h-full"
    />
  );
}

export default function HeroSection() {
  const cards = [
    {
      icon: "ri-money-dollar-circle-line",
      title: "380₽/час",
      desc: "Стабильная почасовая оплата. Выплаты дважды в месяц без задержек",
    },
    {
      icon: "ri-home-smile-line",
      title: "Удалёнка",
      desc: "Работайте из дома, из кафе или из любой точки мира — нужен только интернет",
    },
    {
      icon: "ri-user-add-line",
      title: "Без опыта",
      desc: "Опыт не требуется. Мы всему обучим с нуля и доведём до результата",
    },
    {
      icon: "ri-calendar-check-line",
      title: "Гибкий график",
      desc: "Смены 4/2, 5/2 или подработка. Подберём график под ваш ритм жизни",
    },
  ];

  const tags = ["Официальное оформление", "Оплачиваемое обучение", "Стабильные выплаты 2 раза в месяц"];

  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background-50">
      <FloatingParticles />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-primary-100/40 via-background-50 to-background-100/60" />

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-10 py-24 lg:py-32 max-w-7xl mx-auto">
        <div className="flex justify-center mb-10">
          <div className="inline-block px-8 py-4 rounded-2xl border border-primary-200 bg-background-50 shadow-sm">
            <div className="font-bold text-3xl text-foreground-950 flex items-center gap-2">
              <i className="ri-briefcase-fill text-primary-500 text-4xl w-10 h-10 flex items-center justify-center" />
              WorkTerra
            </div>
          </div>
        </div>

        <div className="text-center mb-14">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-6">
            Станьте оператором чата
            <br />
            <span className="font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-2 inline-block text-primary-500">
              с зарплатой 380₽/час
            </span>
          </h1>
          <p className="text-lg sm:text-xl lg:text-2xl text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            WorkTerra — прямой работодатель. Набираем операторов чата в штат с полным обучением
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-10">
          {cards.map((card) => (
            <div
              key={card.title}
              className="rounded-2xl p-6 lg:p-8 flex flex-col items-center justify-center text-center transition-all duration-300 hover:-translate-y-2 hover:scale-[1.02] cursor-default bg-background-50 border border-primary-100 shadow-sm hover:shadow-md"
            >
              <div className="w-12 h-12 flex items-center justify-center mb-4">
                <i className={`${card.icon} text-primary-500 text-4xl`} />
              </div>
              <h3 className="text-lg lg:text-2xl font-semibold text-foreground-950 mb-3">
                {card.title}
              </h3>
              <p className="text-sm lg:text-base text-foreground-600 leading-relaxed">
                {card.desc}
              </p>
            </div>
          ))}
        </div>

        <div className="rounded-2xl p-6 lg:p-8 mb-8 bg-background-50 border border-primary-100 shadow-sm">
          <div className="flex flex-wrap justify-center gap-4">
            {tags.map((tag) => (
              <div
                key={tag}
                className="flex items-center gap-3 px-5 py-3 rounded-full text-foreground-950 text-sm lg:text-base bg-primary-50 border border-primary-100"
              >
                <span className="text-primary-500 font-bold text-lg">
                  <i className="ri-check-line w-5 h-5 flex items-center justify-center" />
                </span>
                <span>{tag}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl p-6 lg:p-8 text-center shadow-sm bg-background-50 border border-primary-100">
          <p className="text-lg lg:text-2xl text-foreground-950 font-medium leading-relaxed mb-3">
            <span className="font-extrabold text-xl lg:text-3xl text-primary-500">
              WorkTerra
            </span>{" "}
            — мы нанимаем операторов чата напрямую. Никаких посредников
          </p>
          <p className="text-sm lg:text-lg text-foreground-600 leading-relaxed">
            Пройдите обучение, получите оплачиваемую стажировку и начните зарабатывать 380₽/час уже через неделю
          </p>
          <a
            href="#contact"
            className="mt-6 inline-flex items-center px-8 py-3 rounded-full font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 shadow-lg shadow-primary-500/30 transition-colors duration-200 cursor-pointer whitespace-nowrap text-base"
          >
            Откликнуться на вакансию
          </a>
        </div>
      </div>
    </section>
  );
}